import { useEffect, useRef } from "react";
import { Dialog, DialogDescription, DialogTitle } from "@jobenvoy/ui";
import { ArrowRightIcon, CloseIcon, SparkIcon } from "@/components/icons";
import { FitBar, FitLine, SectionCard } from "@/components/posting/parts";
import { actionProps, scopeProps } from "@/lib/controlAnalytics";
import { useDesktop } from "@/lib/hooks/viewport";
import type { ToolFit } from "@/lib/tools/check";
import type { PastedJob } from "@/lib/tools/pastedJob";
import type { KeptJob } from "@/lib/tools/tailorAnswer";
import TailorFields from "./TailorFields";
import { useTailorAnswers } from "./useTailorAnswers";

const TITLE = "Where is this job?";
const LINE = "The tailor writes for this job, and your Applications page keeps it.";

/** Under Continue: what comes next, for a visitor, or whose resume is read, for a member. */
function Foot({ member }: { member: boolean }) {
    return (
        <p className="m-0 text-center text-[12.5px] leading-normal text-fg-faint">
            {member ? (
                "Tailor uses your saved resume, not the text in the box."
            ) : (
                <>
                    Next, a free account.{" "}
                    <span className="font-mono font-semibold text-fg">10</span> a month, free. No
                    card.
                </>
            )}
        </p>
    );
}

/**
 * Where the tailor is told which job it is for: title, company and link.
 * On a desktop it takes the panel's place, with the count over it and a
 * way back to the result. On a phone it is a bottom sheet. The page keeps
 * it mounted, so what was typed is there when it opens again.
 */
export default function TailorForm({
    open,
    fit,
    description,
    resume,
    member,
    kept,
    onSubmit,
    onClose,
}: {
    open: boolean;
    fit: ToolFit | null;
    /** The job's text, as pasted. */
    description: string;
    /** The resume box's text, for the way back from sign-in. */
    resume: string;
    member: boolean;
    /** A job back from the tailor, which fills the form in. */
    kept: KeptJob | null;
    onSubmit: (job: PastedJob) => void;
    onClose: () => void;
}) {
    const desktop = useDesktop();
    const answers = useTailorAnswers(kept);
    // The press that opened the form unmounted its button: the heading takes the cursor.
    const heading = useRef<HTMLHeadingElement>(null);
    useEffect(() => {
        if (open && desktop) heading.current?.focus();
    }, [open, desktop]);
    const fields = (
        <TailorFields
            answers={answers}
            description={description}
            resume={resume}
            member={member}
            onSubmit={onSubmit}
        />
    );
    if (desktop) {
        if (!open) return null;
        return (
            <SectionCard className="gap-4 min-[1024px]:sticky min-[1024px]:top-[84px]">
                {fit && fit.asked > 0 && (
                    <>
                        <div className="flex flex-col gap-[5px]">
                            <FitLine covered={fit.covered} asked={fit.asked}>
                                keywords your resume says
                            </FitLine>
                            <FitBar share={fit.share} height={6} />
                        </div>
                        <div className="h-px bg-line-soft" />
                    </>
                )}
                <div className="flex flex-col gap-0.5">
                    <h2
                        ref={heading}
                        tabIndex={-1}
                        className="m-0 font-display text-card-title font-bold tracking-[-0.01em] outline-none"
                    >
                        {TITLE}
                    </h2>
                    <span className="text-ui text-fg-muted [text-wrap:pretty]">{LINE}</span>
                </div>
                {fields}
                <button
                    {...actionProps("tool_tailor_back_clicked", { source: "TailorForm" })}
                    type="button"
                    onClick={onClose}
                    className="inline-flex items-center justify-center gap-1.5 self-center border-0 bg-transparent p-0 text-[13px] font-semibold text-brand-ink max-[760px]:min-h-11"
                >
                    <ArrowRightIcon className="size-3.5 rotate-180" />
                    Back to my result
                </button>
                <Foot member={member} />
            </SectionCard>
        );
    }
    return (
        <Dialog
            {...scopeProps({ source: "TailorForm" })}
            open={open}
            onClose={onClose}
            className="relative rounded-[22px] border border-line p-6 shadow-dialog duration-[260ms] ease-[cubic-bezier(.2,.9,.25,1.12)] max-[760px]:rounded-b-none max-[760px]:pb-[max(24px,env(safe-area-inset-bottom))] max-[760px]:data-closed:translate-y-full"
            backdropClassName="bg-scrim backdrop-blur-[3px]"
            frameClassName="max-[760px]:!p-0"
            rowClassName="max-[760px]:items-end"
            panelStyle={{ maxWidth: 420 }}
        >
            <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3.5 pr-8">
                    <span
                        className="grid size-11 shrink-0 place-items-center rounded-[13px] bg-grad text-brand-fg"
                        aria-hidden="true"
                    >
                        <SparkIcon className="size-[22px]" />
                    </span>
                    <div className="flex flex-col gap-1">
                        <DialogTitle className="m-0 font-display text-[21px] font-bold leading-tight tracking-[-0.02em] sm:text-[21px]">
                            {TITLE}
                        </DialogTitle>
                        <DialogDescription className="m-0 text-[13.5px] leading-[1.45] text-fg-muted">
                            {LINE}
                        </DialogDescription>
                    </div>
                </div>
                {fields}
                <Foot member={member} />
            </div>
            <button
                {...actionProps("tool_tailor_back_clicked", { source: "TailorForm" })}
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="absolute right-3 top-3 grid size-11 place-items-center rounded-[9px] text-fg-muted transition-colors hover:bg-hover"
            >
                <CloseIcon className="size-5" />
            </button>
        </Dialog>
    );
}
